import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Search, PlusCircle, User, MapPin } from 'lucide-react';
import { useAuth } from '../App';

const TABS = [
  { path: '/', icon: Home, label: 'Home' },
  { path: '/dashboard', icon: Search, label: 'My Events' },
  { path: '/create-event', icon: PlusCircle, label: 'Create', isCreate: true },
  { path: '/events', icon: MapPin, label: 'City', isCity: true },
  { path: '/profile', icon: User, label: 'Profile' },
];

export default function BottomTabBar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    setIsStandalone(mq.matches || window.navigator.standalone === true);

    const handler = (e) => setIsStandalone(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  if (!user || !isStandalone) return null;

  const hiddenPaths = ['/admin', '/login', '/signup', '/verify'];
  if (hiddenPaths.some((p) => location.pathname.startsWith(p))) return null;

  const currentPath = location.pathname;
  const userCity = user?.city || 'City';

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 pb-safe block md:hidden"
      data-testid="bottom-tab-bar"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 8px)' }}
    >
      <div className="flex items-center justify-around h-14">
        {TABS.map((tab) => {
          const isActive =
            currentPath === tab.path ||
            (tab.path === '/' && currentPath === '/') ||
            (tab.path === '/dashboard' && currentPath.startsWith('/dashboard')) ||
            (tab.path === '/events' && currentPath.startsWith('/events'));

          const Icon = tab.icon;
          const label = tab.isCity ? userCity : tab.label;

          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                tab.isCreate ? '' : isActive ? 'text-violet-600' : 'text-gray-400'
              }`}
              data-testid={`tab-${tab.label.toLowerCase()}`}
            >
              {tab.isCreate ? (
                <div className="w-12 h-12 -mt-5 rounded-full bg-violet-600 flex items-center justify-center shadow-lg border-4 border-white">
                  <Icon className="w-6 h-6 text-white" />
                </div>
              ) : (
                <>
                  <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 1.5} />
                  <span className={`text-[10px] mt-0.5 max-w-[60px] truncate ${isActive ? 'font-bold' : 'font-medium'}`}>
                    {label}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
